import {
  renderCachedUserAvatarUrl,
  revokeCachedUserAvatarObjectUrl,
  UserAvatarCacheProvider,
  UserProvider,
  WriterInfoProvider,
} from '@blocksuite/affine-shared/services';
import {
  BaseCellRenderer,
  createFromBaseCellRenderer,
  createIcon,
} from '@blocksuite/data-view';
import { UserIcon } from '@blocksuite/icons/lit';
import { css } from '@emotion/css';
import { html } from 'lit';

import { createdByPropertyModelConfig } from './define.js';

const requestedUserIds = new Set<string>();

const createdByCellStyle = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
});

const textStyle = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  gap: '6px',
  fontSize: 'var(--streamlain-font-base)',
  lineHeight: 'var(--streamlain-line-height)',
  color: 'var(--streamlain-text-primary-color)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const avatarStyle = css({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
  backgroundColor: 'var(--streamlain-background-secondary-color)',
  color: 'var(--streamlain-text-secondary-color)',
});

const avatarImageStyle = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export class CreatedByCell extends BaseCellRenderer<string, string> {
  private resolveUserInfo(userId: string) {
    const userService = this.view.serviceGet(UserProvider);
    if (userService) {
      if (!requestedUserIds.has(userId)) {
        requestedUserIds.add(userId);
        userService.revalidateUserInfo(userId);
      }
      const currentUser = userService.currentUserInfo$.value;
      if (currentUser?.id === userId) {
        return currentUser;
      }
      const userInfo = userService.userInfo$(userId).value;
      if (userInfo) {
        return userInfo;
      }
    }
    const writerService = this.view.serviceGet(WriterInfoProvider);
    const writerInfo = writerService?.getWriterInfo() ?? null;
    if (writerInfo?.id === userId) {
      return writerInfo;
    }
    return null;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.classList.add(createdByCellStyle);
  }

  override beforeEnterEditMode() {
    return false;
  }

  override render() {
    const userId = this.value;
    if (!userId) {
      return html`<div class="${textStyle}"></div>`;
    }
    const userInfo = this.resolveUserInfo(userId);
    const label = userInfo?.removed
      ? 'Inactive Member'
      : (userInfo?.name ?? userId);
    const avatar = (userInfo as { avatar?: string; id?: string } | undefined)
      ?.avatar;
    const avatarCache = this.view.serviceGet(UserAvatarCacheProvider);
    return html`<div class="${textStyle}">
      <span class="${avatarStyle}">
        ${avatar
          ? renderCachedUserAvatarUrl({
              avatarCache,
              avatarUrl: avatar,
              userId,
              render: (url, cached) =>
                html`<img
                  class="${avatarImageStyle}"
                  src=${url}
                  alt=""
                  @load=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
                  @error=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
                />`,
            })
          : UserIcon()}
      </span>
      <span>${label}</span>
    </div>`;
  }
}

export const createdByColumnConfig =
  createdByPropertyModelConfig.createPropertyMeta({
    icon: createIcon('UserIcon'),
    cellRenderer: {
      view: createFromBaseCellRenderer(CreatedByCell),
    },
  });
